import React from 'react';
import { FlagBrazil, FlagEua, FlagSpanish, IconFacebook2, IconInstagram } from '@/assets/icons';
import { NavigationMenu } from 'src/types';
import logo from '../../assets/images/logo.svg';

interface FooterProps {
  handleOnSignIn: () => void;
  handleOnSignUp: () => void;
  goTo: (href: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ handleOnSignIn, handleOnSignUp, goTo }) => {
  const navigation: NavigationMenu[] = [
    { name: 'Início', href: '/', current: true },
    { name: 'Quem somos', href: '/quemsomos', current: false },
    {
      name: 'Contato e suporte',
      href: '/contato-suporte',
      current: false,
    },
    { name: 'Políticas', href: '/politicas', current: false },
  ];

  return (
    <footer className="w-full bg-white border-t border-neutro-w-200">
      {/* Top Footer Section */}
      <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          {/* Identity & Legal (Left Column) */}
          <div className="md:col-span-5 flex flex-col space-y-6">
            <img
              src={logo}
              alt="Banca do Ingresso"
              className="h-[90px] w-auto object-contain self-start cursor-pointer transition-transform hover:scale-105"
              onClick={() => goTo('/')}
            />
            <p className="text-sm font-dmsans text-paragraph-footer leading-relaxed max-w-sm">
              Banca do Ingresso e Eventos Ltda
              <br />
              CNPJ 10.901.355/0001-05
              <br />
              Rua Perimetral Leste, 577 - Centro
              <br />
              Santa Fe do Sul - SP, CEP 15.775-000
            </p>
          </div>

          {/* Navigation Links */}
          <div className="md:col-span-7 grid grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Site Links */}
            <div className="flex flex-col">
              <h5 className="text-neutro-b-500 font-bold font-dmsans mb-5 text-sm uppercase tracking-wider">
                Site
              </h5>
              <nav className="flex flex-col space-y-3">
                {navigation.map(item => (
                  <a
                    key={item.name}
                    href="#"
                    className="text-paragraph-footer font-normal font-dmsans text-base transition-colors duration-200 hover:text-primary-500"
                    onClick={e => {
                      e.preventDefault();
                      goTo(item.href);
                    }}
                  >
                    {item.name}
                  </a>
                ))}
              </nav>
            </div>

            {/* Account Links */}
            <div className="flex flex-col">
              <h5 className="text-neutro-b-500 font-bold font-dmsans mb-5 text-sm uppercase tracking-wider">
                Minha Conta
              </h5>
              <nav className="flex flex-col space-y-3">
                <a
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleOnSignUp();
                  }}
                  className="text-paragraph-footer font-normal font-dmsans text-base transition-colors duration-200 hover:text-primary-500"
                >
                  Criar conta
                </a>
                <a
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleOnSignIn();
                  }}
                  className="text-paragraph-footer font-normal font-dmsans text-base transition-colors duration-200 hover:text-primary-500"
                >
                  Entrar
                </a>
              </nav>
            </div>

            {/* Payments & Info */}
            <div className="flex flex-col col-span-2 lg:col-span-1 mt-4 lg:mt-0">
              <h5 className="text-neutro-b-500 font-bold font-dmsans mb-5 text-sm uppercase tracking-wider">
                Pagamento Seguro
              </h5>
              <nav className="flex flex-col space-y-3">
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://pagseguro.uol.com.br/"
                  className="text-paragraph-footer font-normal font-dmsans text-base transition-colors duration-200 hover:text-primary-500"
                >
                  PagSeguro
                </a>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.c6bank.com.br"
                  className="text-paragraph-footer font-normal font-dmsans text-base transition-colors duration-200 hover:text-primary-500"
                >
                  C6 Bank
                </a>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer Section */}
      <div className="border-t border-neutro-w-200 bg-neutro-w-000">
        <div className="container mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Copyright */}
          <p className="text-paragraph-footer font-normal font-dmsans text-sm text-center md:text-left">
            © {new Date().getFullYear()} Banca do Ingresso - Todos os direitos reservados.
          </p>

          {/* Social Media & Languages */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Languages */}
            <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-full shadow-sm border border-neutro-w-150">
              <span className="text-xs font-dmsans text-neutro-w-600 mr-2 uppercase font-medium">
                Idioma:
              </span>
              <a href="#" className="hover:opacity-80 transition-opacity">
                <img
                  src={FlagBrazil}
                  alt="Português Brasil"
                  className="w-6 h-6 object-cover rounded-full"
                />
              </a>
              <a href="#" className="hover:opacity-80 transition-opacity">
                <img
                  src={FlagSpanish}
                  alt="Espanhol"
                  className="w-6 h-6 object-cover rounded-full"
                />
              </a>
              <a href="#" className="hover:opacity-80 transition-opacity">
                <img
                  src={FlagEua}
                  alt="Inglês americano"
                  className="w-6 h-6 object-cover rounded-full"
                />
              </a>
            </div>

            {/* Social */}
            <div className="flex space-x-3">
              <a
                href="#"
                className="bg-white rounded-full flex justify-center items-center w-[36px] h-[36px] shadow-sm border border-neutro-w-150 hover:border-primary-500 hover:bg-primary-50 transition-all group"
                onClick={e => {
                  e.preventDefault();
                  window.open('https://www.instagram.com/bancadoingresso/');
                }}
              >
                <IconInstagram className="h-[18px] text-primary-500 group-hover:scale-110 transition-transform" />
              </a>
              <a
                href="#"
                className="bg-white rounded-full flex justify-center items-center w-[36px] h-[36px] shadow-sm border border-neutro-w-150 hover:border-primary-500 hover:bg-primary-50 transition-all group"
                onClick={e => {
                  e.preventDefault();
                  window.open('https://www.facebook.com/bancadoingresso.com.br');
                }}
              >
                <IconFacebook2 className="h-[20px] text-primary-500 group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
